<template>
  <TModal
    v-model:visible="visible"
    :has-priority="shouldTrapFocus"
  >
    <AtomTransition appear>
      <TModalBackground
        v-if="isVisible && !isAnimating"
        variant="light"
        @click="handleBackgroundClick"
      />
    </AtomTransition>

    <div
      ref="modalRef"
      class="h-full"
    >
      <TModalContentsWrapper :class="wrapperClasses">
        <AtomTransition
          appear
          variant="fade-scale"
          @after-leave="handleAfterLeave"
        >
          <TModalContents
            v-if="isVisible && !isAnimating"
            :class="contentClasses"
          >
            <TModalHeader
              :class="headerClasses"
              enable-close-button
              :title="title"
              @close="closeWithExitButton"
            />

            <TModalBody
              :class="bodyClasses"
              :enable-scroll="enableScroll"
            >
              <slot :dismiss="closeWithButton" />
            </TModalBody>

            <TModalFooter
              v-if="hasFooter"
              :class="footerClasses"
              :label-primary="labelPrimary"
              @action:primary="() => emit('action:primary', closeWithButton)"
            />
          </TModalContents>
        </AtomTransition>
      </TModalContentsWrapper>
    </div>
  </TModal>
</template>

<script setup lang="ts">
import { useModal } from './useModal'
import type { ModalCloseTrigger, ModalSize } from './types'

/**
 * This modal is a bit more informal and can be used to display non-critical
 * information, like the 3D tour on RV's.
 *
 * Eventually this modal type can be used to display additional information that
 * we now store in tooltips, for example.
 */
const props = withDefaults(defineProps<{
  title: string
  subtitle?: string
  size?: ModalSize
  labelPrimary?: string
  enableScroll?: boolean
}>(), {
  size: 'md',
})

const emit = defineEmits<{
  'close': [trigger: ModalCloseTrigger]
  'action:primary': [dismiss: () => void]
}>()

const visible = defineModel<boolean>('visible', { default: false })
const modalRef = useTemplateRef<HTMLDivElement>('modalRef')

const {
  wrapperClasses,
  contentClasses,
  headerClasses,
  bodyClasses,
  footerClasses,

  isVisible,
  isAnimating,

  handleAfterLeave,
  handleBackgroundClick,
  closeWithExitButton,
  closeWithButton,
  shouldTrapFocus,
} = useModal({
  enableEscapeKey: true,
  enableOutsideDismiss: true,
  enableScrollLock: true,
  mobileType: 'overlay',
  size: toRef(props, 'size'),
  visible,
  modalRef,
  focusTrapOptions: { clickOutsideDeactivates: true, escapeDeactivates: true },
  onClose: (trigger) => {
    emit('close', trigger)
  },
})

const hasFooter = computed(() => {
  return props.labelPrimary
})
</script>
